import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TFunction } from 'next-i18next';
import toast from 'react-hot-toast';
import InfiniteScroll from 'react-infinite-scroll-component';
import * as yup from 'yup';
import 'dayjs/locale/vi';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Button,
  Icon,
  InputVariant,
  SendIcon,
  TextField,
  XNewIcon,
} from '@/components';
import {
  VideoReelCommentModel,
  VideoReelModel,
  VideoReelReplyCommentModel,
} from '@/models';
import { useProfile } from '@/contexts/profile';
import { ReelMethod, SocketChanel } from '@/layouts/shared';
import { getAuth } from '@/libs/cookies';

import {
  useCreateComment,
  useCreateReplyComment,
  useFetchCommentsQuery,
  useFetchReplyCommentsQuery,
  useFetchVideoReelQuery,
} from '../api';
import Comment from './Comment';
import { ActionType } from '../const';

interface CommentsProps {
  video: VideoReelModel;
  show: boolean;
  t: TFunction;
  dispatch: (value: any) => void;
  onCloseComment: () => void;
}

enum InputType {
  CommentInput,
  ReplyInput,
}

const Comments = ({
  video,
  show,
  t,
  dispatch,
  onCloseComment,
}: CommentsProps) => {
  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      comment: '',
    },
    resolver: yupResolver(
      yup.object({
        comment: yup.string().trim().required(t('message.comment-required')),
      })
    ),
  });

  const { token, isAuth } = getAuth();
  const profile: any = useProfile();

  const commentsContainerRef = useRef<any>(null);
  const commentRef = useRef<any>(null);
  const clientRef = useRef<any>(null);
  const timeout = useRef<any>(null);

  const [comments, setComments] = useState<VideoReelCommentModel[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [commentToExpand, setCommentToExpand] =
    useState<VideoReelCommentModel | null>(null);
  const [replyCommentInput, setReplyCommentInput] = useState<any>(null);
  const [replyCommentContent, setReplyCommentContent] = useState<string>('');
  const [editCommentInput, setEditCommentInput] = useState<any>(null);
  const [editCommentContent, setEditCommentContent] = useState<string>('');
  const [waitingToReconnect, setWaitingToReconnect] = useState<boolean | null>(
    null
  );

  const { refetch: fetchComments, isLoading: isLoadingComments } =
    useFetchCommentsQuery(
      {
        reelId: video.id,
        params: { offset: comments.length, reply_limit: 50 },
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
        onError: ({ message }) => {
          toast.error(message);
        },
        onSuccess: ({ data, total_records }) => {
          const _comments = Array.isArray(data) ? data : [];
          setComments((prev) => [...prev, ..._comments]);
          setTotal(total_records);
        },
      }
    );

  const { refetch: fetchReplyComments, isLoading: isLoadingReplyComments } =
    useFetchReplyCommentsQuery(
      {
        commentId: Number(commentToExpand?.id),
        params: { limit: commentToExpand?.reply_comment_count },
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
        onError: ({ message }) => {
          toast.error(message);
        },
        onSuccess: ({ data, total_records }) => {
          const replies = Array.isArray(data) ? data : [];
          setRepliesToComment(Number(commentToExpand?.id), replies);
        },
      }
    );

  const { refetch: getVideoDetail, isLoading: isLoadingVideoDetail } =
    useFetchVideoReelQuery(video.id, {
      enabled: false,
      refetchOnWindowFocus: false,
      onError: ({ message }) => {
        toast.error(message);
      },
      onSuccess: ({ data }) => {
        dispatch({ type: ActionType.UpdateVideo, payload: data });
      },
    });

  const { mutate: createComment, isLoading: isCreatingComment } =
    useCreateComment();

  const { mutate: createReplyComment, isLoading: isCreatingReplyComment } =
    useCreateReplyComment();

  useEffect(() => {
    if (show) {
      fetchComments();
    } else {
      setComments([]);
      setTotal(0);
      setReplyCommentInput(null);
      setEditCommentInput(null);
    }
  }, [show, fetchComments]);

  useEffect(() => {
    if (commentToExpand?.id) {
      fetchReplyComments();
    }
  }, [commentToExpand, fetchReplyComments]);

  useEffect(() => {
    if (replyCommentInput) {
      setEditCommentInput(null);
    } else {
      setReplyCommentContent('');
    }
  }, [replyCommentInput]);

  useEffect(() => {
    if (editCommentInput) {
      setReplyCommentInput(null);
    } else {
      setEditCommentContent('');
    }
  }, [editCommentInput]);

  useEffect(() => {
    if (waitingToReconnect || clientRef.current || !isAuth || !show) return;

    const ws = new WebSocket(
      `${
        process.env.WEBSOCKET_URL || 'wss://api-dev.anthu.tech/ws/realtime'
      }?token=${token}`
    );

    clientRef.current = ws;

    ws.onopen = () => {
      const socketPayload = {
        method: 'subscribe',
        type: 'unicast',
        channels: [{ id: SocketChanel.Reel, object_id: video.id }],
      };
      ws.send(JSON.stringify(socketPayload));
      timeout.current = setInterval(() => {
        let socketPayload = { method: 'ping' };
        ws.send(JSON.stringify(socketPayload));
      }, 15000);
      console.log('open connect');
    };

    ws.onmessage = (event: any) => {
      const jsonData = JSON.parse(event.data);

      console.log('json: ', jsonData);

      if (typeof jsonData === 'object' && jsonData?.data) {
        if (jsonData.method === ReelMethod.CreateComment) {
          setComments((prev) => [...[jsonData.data], ...prev]);
          getVideoDetail();
          return;
        }

        if (jsonData.method === ReelMethod.CreateRelyComment) {
          setComments((prev) => {
            const newCommentList = prev?.map(
              (comment: VideoReelCommentModel) => {
                if (comment.id === jsonData.data?.reel_comment_id) {
                  return {
                    ...comment,
                    reply_comment_count: comment.reply_comment_count + 1,
                    reel_reply_comments: [
                      ...[jsonData.data],
                      ...(comment.reel_reply_comments ?? []),
                    ],
                  };
                }
                return comment;
              }
            );
            return newCommentList;
          });
          getVideoDetail();
          return;
        }

        if (jsonData.method === ReelMethod.UpdateComment) {
          setComments((prev) => {
            return prev.map((comment: VideoReelCommentModel) => {
              if (comment.id === jsonData.data.id) {
                return {
                  ...comment,
                  ...jsonData.data,
                };
              }
              return comment;
            });
          });
          getVideoDetail();
          return;
        }

        if (jsonData.method === ReelMethod.UpdateRelyComment) {
          setComments((prev) => {
            const newComments = prev.map((comment: VideoReelCommentModel) => {
              if (comment.id === jsonData.data.reel_comment_id) {
                return {
                  ...comment,
                  reel_reply_comments: comment.reel_reply_comments?.map(
                    (reply: VideoReelReplyCommentModel) => {
                      if (reply.id === jsonData.data.id) {
                        return jsonData.data;
                      }
                      return reply;
                    }
                  ),
                };
              }
              return comment;
            });
            return newComments;
          });
          getVideoDetail();
          return;
        }

        if (jsonData.method === ReelMethod.DeleteComment) {
          setComments((prev) => {
            return prev.filter(
              (comment: VideoReelCommentModel) =>
                comment.id !== jsonData.data.id
            );
          });
          getVideoDetail();
          return;
        }

        if (jsonData.method === ReelMethod.HideComment) {
          setComments((prev) => {
            return prev.filter(
              (comment: VideoReelCommentModel) =>
                comment.id !== jsonData.data.id
            );
          });
          getVideoDetail();
          return;
        }

        if (jsonData.method === ReelMethod.DeleteRelyComment) {
          setComments((prev) => {
            const newComments = prev.map((comment: VideoReelCommentModel) => {
              if (comment.id === jsonData.data.comment_id) {
                return {
                  ...comment,
                  reply_comment_count: comment.reply_comment_count - 1,
                  reel_reply_comments: comment.reel_reply_comments?.filter(
                    (reply: VideoReelReplyCommentModel) =>
                      reply.id !== jsonData.data.reply_comment_id
                  ),
                };
              }
              return comment;
            });
            return newComments;
          });
          getVideoDetail();
          return;
        }

        if (jsonData.method === ReelMethod.HideReplyComment) {
          setComments((prev) => {
            const newComments = prev.map((comment: VideoReelCommentModel) => {
              if (comment.id === jsonData.data.reel_comment_id) {
                return {
                  ...comment,
                  reply_comment_count: comment.reply_comment_count - 1,
                  reel_reply_comments: comment.reel_reply_comments?.filter(
                    (reply: VideoReelReplyCommentModel) =>
                      reply.id !== jsonData.data.id
                  ),
                };
              }
              return comment;
            });
            return newComments;
          });
          getVideoDetail();
          return;
        }
      }
    };

    ws.onclose = () => {
      if (clientRef.current) {
        console.log('ws closed by server');
      } else {
        console.log('ws closed by app component unmount');
        return;
      }
      setWaitingToReconnect(true);
      setTimeout(() => setWaitingToReconnect(null), 5000);
    };

    return () => {
      ws.close();
      clearInterval(timeout.current);
      clientRef.current = null;
      timeout.current = null;
    };
  }, [video.id, waitingToReconnect, show, getVideoDetail, isAuth, token]);

  const handleLoadMoreComments = () => {
    fetchComments();
  };

  const setRepliesToComment = (
    commentId: number,
    replies: VideoReelReplyCommentModel[]
  ) => {
    setComments((prev) => {
      return prev.map((comment: VideoReelCommentModel) => {
        if (comment.id === commentId) {
          comment.reel_reply_comments = [...replies];
        }
        return comment;
      });
    });
  };

  const sendComment = handleSubmit((data) => {
    if (!isAuth) {
      return toast.error(t('message.unauthorized'));
    }

    if (!data.comment) return;
    createComment({ reelId: video.id, body: data });
    reset();
  });

  const sendReplyComment = () => {
    if (!replyCommentInput || !replyCommentContent) return;
    createReplyComment({
      commentId: replyCommentInput.commentId,
      body: { comment: replyCommentContent },
    });
    setReplyCommentInput(null);
  };

  const handleKeyPress = (e: any, type: InputType) => {
    if (e.keyCode === 13) {
      if (type === InputType.CommentInput) {
        sendComment();
      } else if (type === InputType.ReplyInput) {
        sendReplyComment();
      }
    }
  };

  const handleShowReplyInput = (
    comment: VideoReelCommentModel | VideoReelReplyCommentModel
  ) => {
    const commentId = (comment as any).reel_comment_id || comment.id;

    if (replyCommentInput?.replyId === comment.id) {
      setReplyCommentInput({
        commentId: commentId,
        replyId: comment.id,
        isShow: !replyCommentInput?.isShow,
        taggedUser: comment.users || comment.staffs,
      });
    }

    if (replyCommentInput?.replyId !== comment.id) {
      setReplyCommentInput({
        commentId: commentId,
        replyId: comment.id,
        isShow: true,
        taggedUser: comment.users || comment.staffs,
      });
    }
  };

  useEffect(() => {
    if (replyCommentInput?.isShow) {
      if (replyCommentInput.taggedUser) {
        setReplyCommentContent(
          '@' + replyCommentInput.taggedUser.fullname + ' '
        );
      }
    } else {
      setReplyCommentContent('');
    }
  }, [replyCommentInput]);

  const isShowReplyInput = (comment: VideoReelCommentModel) => {
    return (
      replyCommentInput?.isShow && replyCommentInput?.commentId === comment.id
    );
  };

  return (
    <Fragment>
      <div className="tw-w-full tw-h-full tw-border tw-border-[#EEF0F5] tw-rounded-t-lg sm:tw-rounded-lg tw-overflow-hidden">
        <div className="tw-flex tw-flex-col tw-h-full tw-w-full">
          <div className="tw-flex tw-items-center tw-justify-between tw-h-[40px] tw-px-4 tw-py-2 tw-bg-[#EEF0F5]">
            <div className="tw-text-secondary-70 tw-text-sm">
              <span className="tw-font-semibold">{t('label.comment')}</span>
              <span className="tw-ml-2 tw-font-light">
                {video.total_comment_count}
              </span>
            </div>
            <div className="tw-cursor-pointer" onClick={onCloseComment}>
              <Icon icon={<XNewIcon />} className="tw-w-6 tw-h-6" />
            </div>
          </div>
          <div
            id={`scroll-container-${video.id}`}
            ref={commentsContainerRef}
            className="tw-flex-1 tw-p-0 tw-h-[calc(100%-40px-48px)] tw-overflow-y-hidden tw-bg-white"
          >
            {comments.length > 0 ? (
              <InfiniteScroll
                dataLength={comments.length}
                next={handleLoadMoreComments}
                hasMore={comments.length < total}
                loader={<></>}
                scrollableTarget={`scroll-container-${video.id}`}
                inverse={true}
                className="tw-h-full tw-flex tw-flex-col-reverse tw-px-4 tw-py-2"
                height={commentsContainerRef?.current?.offsetHeight || 32}
                ref={commentRef}
              >
                {comments.map((comment) => (
                  <Comment
                    key={comment.id}
                    comment={comment}
                    isChild={false}
                    isShowReplyInput={isShowReplyInput(comment)}
                    setReplyCommentInput={setReplyCommentInput}
                    replyCommentContent={replyCommentContent}
                    setReplyCommentContent={setReplyCommentContent}
                    editCommentInput={editCommentInput}
                    setEditCommentInput={setEditCommentInput}
                    editCommentContent={editCommentContent}
                    setEditCommentContent={setEditCommentContent}
                    onShowReplyInput={handleShowReplyInput}
                    onSetReplies={setRepliesToComment}
                    onSendReplyComment={sendReplyComment}
                    t={t}
                    profile={profile}
                    isAuth={isAuth}
                  />
                ))}
              </InfiniteScroll>
            ) : (
              <div className="tw-px-4 tw-py-2">{t('message.no-comments')}</div>
            )}
          </div>
          <div className="tw-flex tw-items-center tw-gap-x-2 tw-h-[49px] tw-px-4 tw-bg-[#EEF0F5] tw-border-t">
            <TextField
              name="comment"
              control={control}
              showErrorMessage={false}
              variant={InputVariant.Outlined}
              placeholder={t('placeholder.comment') + '...'}
              wrapperClassName="tw-flex-grow tw-h-10"
              containerClassName="tw-bg-white tw-rounded-full !tw-border-0 tw-border-white tw-flex-1"
              className="tw-px-2 tw-text-sm custom-placeholder"
              onKeyDown={(e) => handleKeyPress(e, InputType.CommentInput)}
            />
            <Button
              className="tw-flex tw-justify-center tw-p-2 !tw-rounded-full !tw-w-fit tw-bg-primary-main"
              onClick={() => {
                sendComment();
              }}
            >
              <Icon className="tw-w-4 tw-h-4" icon={<SendIcon />} />
            </Button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Comments;
